import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { DocumentTypeBadge } from '../components/shared/DocumentTypeBadge';

type DocType = 'all' | 'policy' | 'sop' | 'work_instruction' | 'form' | 'reference' | 'unknown';

const TABS: { value: DocType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'policy', label: 'Policies' },
  { value: 'sop', label: 'SOPs' },
  { value: 'work_instruction', label: 'Work Instructions' },
  { value: 'form', label: 'Forms' },
  { value: 'reference', label: 'Reference' },
];

export default function DocumentLibraryPage() {
  const [activeTab, setActiveTab] = useState<DocType>('all');
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['document-classifications', activeTab],
    queryFn: () => api.documents.classifications(activeTab === 'all' ? undefined : activeTab),
    staleTime: 60_000,
  });

  const confirmMutation = useMutation({
    mutationFn: ({ id, documentType }: { id: string; documentType?: string }) =>
      api.documents.confirm(id, documentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-classifications'] });
      toast.success('Classification confirmed');
    },
  });

  const docs = (data?.data ?? []).filter((d: Record<string, unknown>) =>
    !search || (d.fileName as string).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Document Library</h1>
          <p className="text-sm text-muted-foreground">Classified documents across connected repositories</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === tab.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="w-full rounded-md border border-border bg-background px-3 py-2 pl-9 text-sm"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="h-12 w-12 text-muted-foreground/30" />
          <p className="mt-4 text-lg font-medium">No documents classified</p>
          <p className="text-sm text-muted-foreground">Connect a document repository and run classification</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((doc: Record<string, unknown>) => (
            <div key={doc.id as string} className="flex items-center gap-4 rounded-lg border border-border bg-card p-4">
              <FileText className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{doc.fileName as string}</p>
                <p className="text-xs text-muted-foreground truncate">{doc.filePath as string}</p>
              </div>
              <DocumentTypeBadge type={doc.documentType as string} />
              <span className="text-xs text-muted-foreground">
                {Math.round((doc.classificationConfidence as number) * 100)}%
              </span>
              {doc.confirmedByHuman ? (
                <span className="flex items-center gap-1 text-xs text-emerald-400">
                  <Check className="h-3 w-3" /> Confirmed
                </span>
              ) : (
                <button
                  onClick={() => confirmMutation.mutate({ id: doc.id as string })}
                  className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                  aria-label="Confirm classification"
                >
                  Confirm
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
